import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  CreditCard,
  Calendar,
  FileText,
  User,
  Wallet,
  Receipt,
  Clock,
  Hash,
  Building2,
  FileCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { InfoField } from '@/components/common/InfoField';
import { QuickLinkButton } from '@/components/common/QuickLinkButton';
import { formatCurrency } from '@/lib/utils/currency';
import { usePayment } from '../hooks';
import { PaymentStatusBadge } from '../components';

export function PaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: payment, isLoading, isError, error } = usePayment(id);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header Skeleton */}
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !payment) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <CreditCard className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">
                {(error as Error)?.message || t('failedToLoadPayment')}
              </p>
            </div>
            <Link to="/app/payments">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToPayments')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formattedAmount = formatCurrency(payment.amount, {
    locale: 'uz-UZ',
    currency: payment.currency,
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Back Button */}
          <Link
            to="/app/payments"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToPayments')}
          </Link>

          {/* Payment Info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {/* Icon */}
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center border-4 border-white shadow-lg">
                <Wallet className="h-8 w-8 text-green-600" />
              </div>

              {/* Amount and Badges */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {formattedAmount}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Payment ID Badge */}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                    <Hash className="h-3.5 w-3.5" />
                    {t('payment')} #{String(payment.id).padStart(5, '0')}
                  </span>
                  {/* Status Badge */}
                  <PaymentStatusBadge status={payment.status} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {payment.paidAt && (
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center sm:justify-start gap-8">
              <div className="text-center sm:text-left">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                  {t('paidAt')}
                </p>
                <p className="text-xl font-bold text-slate-900">
                  {format(new Date(payment.paidAt), 'MMM d, yyyy')}
                </p>
              </div>
              <Separator orientation="vertical" className="h-10 hidden sm:block" />
              <div className="text-center sm:text-left hidden sm:block">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                  {t('currency')}
                </p>
                <p className="text-xl font-bold text-slate-900">{payment.currency}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards Grid */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Payment Details Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-slate-400" />
                {t('paymentInformation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField
                icon={Wallet}
                label={t('amount')}
                value={formattedAmount}
              />
              <InfoField
                icon={FileText}
                label={t('description')}
                value={payment.description || '-'}
              />
              {payment.reference && (
                <InfoField
                  icon={FileCheck}
                  label={t('reference')}
                  value={payment.reference}
                  mono
                />
              )}
              {payment.paymentMethod && (
                <InfoField
                  icon={CreditCard}
                  label={t('paymentMethod')}
                  value={payment.paymentMethod.name}
                />
              )}
            </CardContent>
          </Card>

          {/* Related Entities Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-slate-400" />
                {t('relatedEntities')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {payment.bill && (
                <div className="flex items-start gap-3">
                  <Receipt className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                      {t('bill')}
                    </p>
                    <Link
                      to={`/app/bills/${payment.bill.id}`}
                      className="text-sm font-medium text-blue-600 hover:underline"
                    >
                      #{String(payment.bill.id).padStart(5, '0')}
                    </Link>
                  </div>
                </div>
              )}
              {payment.client && (
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                      {t('client')}
                    </p>
                    <Link
                      to={`/app/clients/${payment.client.id}`}
                      className="text-sm font-medium text-blue-600 hover:underline"
                    >
                      {payment.client.name || t('unnamed')}
                    </Link>
                  </div>
                </div>
              )}
              {payment.notes && (
                <InfoField
                  icon={FileText}
                  label={t('notes')}
                  value={payment.notes}
                />
              )}
            </CardContent>
          </Card>

          {/* System Information Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-400" />
                {t('systemInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField label={t('paymentId')} value={String(payment.id)} mono />
              {payment.paidAt && (
                <InfoField
                  icon={Calendar}
                  label={t('paidAt')}
                  value={format(new Date(payment.paidAt), 'MMM d, yyyy HH:mm')}
                />
              )}
              <InfoField
                label={t('createdAt')}
                value={
                  payment.createdAt
                    ? format(new Date(payment.createdAt), 'MMM d, yyyy HH:mm')
                    : '-'
                }
              />
              <InfoField
                label={t('updatedAt')}
                value={
                  payment.updatedAt ? (
                    <span>
                      {format(new Date(payment.updatedAt), 'MMM d, yyyy HH:mm')}
                      <span className="text-slate-400 text-xs ml-2">
                        ({formatDistanceToNow(new Date(payment.updatedAt), { addSuffix: true })})
                      </span>
                    </span>
                  ) : (
                    '-'
                  )
                }
              />
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900">
                {t('quickActions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {payment.bill && (
                  <QuickLinkButton
                    icon={Receipt}
                    label={t('viewBill')}
                    to={`/app/bills/${payment.bill.id}`}
                  />
                )}
                {payment.client && (
                  <QuickLinkButton
                    icon={User}
                    label={t('viewClient')}
                    to={`/app/clients/${payment.client.id}`}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
